import { useDeleteCannedWorkPackagesMutation } from "@common/api/quoting/canned"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import z from "zod"
import { DeleteDialog } from "@/components/layout/list-table-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"

const searchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export const Route = createFileRoute("/_layout/templates/quotes/delete")({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    await requirePermission(queryClient, "canned_work_package:delete", "/")
    if (search.ids.length === 0) {
      throw redirect({ to: "/templates/quotes", search: true })
    }
  },
  component: DeleteTemplateQuotesDialog,
})

function counted(count: number): string {
  return `${formatCount(count)} template quote${count === 1 ? "" : "s"}`
}

function DeleteTemplateQuotesDialog(): React.ReactElement {
  const { ids } = Route.useSearch()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const remove = useDeleteCannedWorkPackagesMutation()

  const close = () =>
    navigate({
      from: Route.fullPath,
      to: "/templates/quotes",
      search: ({ ids: _, ...previous }) => previous,
    })

  return (
    <DeleteDialog
      noun={ids.length === 1 ? "template quote" : counted(ids.length)}
      pending={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        remove.mutate(
          { cannedWorkPackageBulkIds: { canned_work_package_ids: ids } },
          {
            onSuccess: ({ deleted }) => {
              showToast(
                "Deleted",
                `${counted(deleted.length)} deleted.`,
                "success",
              )
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    />
  )
}
